import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowUpRight, History, Undo2, CheckCircle2, XCircle, RotateCcw, Ban,
  GraduationCap, ShieldCheck, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

function generateYears() {
  const cur = new Date().getFullYear();
  const years = [];
  for (let y = cur - 2; y <= cur + 3; y++) years.push(`${y}/${y + 1}`);
  return years;
}

const ACTION_LABELS = {
  promote: "ترحيل للصف التالي",
  repeat: "إعادة نفس الصف",
  graduate: "تخرّج",
  exclude: "استبعاد (بلا نقل)",
};

/**
 * ترحيل الطلاب لعام دراسي جديد — يعتمد بالكامل على نفس منطق النجاح/الرسوب
 * والانتقال بين المراحل المستخدَم أصلًا في شهادة نهاية العام. كل قرار
 * (ترحيل/إعادة/تخرّج/استبعاد) قابل للتعديل يدويًا قبل التنفيذ النهائي —
 * لا شيء يُنفَّذ تلقائيًا بلا مراجعة المستخدم.
 */
export default function Promotion() {
  const { ctx, meta } = useApp();
  const grades = meta?.grades_by_stage?.[ctx.stage] || [];
  const years = generateYears();

  const [sourceGrade, setSourceGrade] = useState("");
  const [targetYear, setTargetYear] = useState("");
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null); // نتيجة /promotion/preview
  const [decisions, setDecisions] = useState({}); // student_id -> {action, target_stage, target_grade}
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);

  const [opsOpen, setOpsOpen] = useState(false);
  const [ops, setOps] = useState([]);
  const [opsLoading, setOpsLoading] = useState(false);

  useEffect(() => {
    // افتراض معقول لعام الترحيل: العام التالي مباشرة بعد عام السياق الحالي
    const [start] = (ctx.year || "").split("/");
    if (start) setTargetYear(`${Number(start) + 1}/${Number(start) + 2}`);
  }, [ctx.year]);

  const runPreview = async () => {
    if (!sourceGrade) return toast.error("اختر الصف أولًا");
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.get("/promotion/preview", {
        params: { stage: ctx.stage, year: ctx.year, grade: sourceGrade },
      });
      setPreview(data);
      const initial = {};
      data.students.forEach((s) => {
        initial[s.student_id] = {
          action: s.default_action,
          target_stage: s.default_target_stage,
          target_grade: s.default_target_grade,
        };
      });
      setDecisions(initial);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const updateDecision = (studentId, patch) => {
    setDecisions((prev) => ({ ...prev, [studentId]: { ...prev[studentId], ...patch } }));
  };

  const setActionFor = (studentId, action) => {
    if (action === "promote") {
      updateDecision(studentId, {
        action, target_stage: preview.target_stage_if_promoted, target_grade: preview.target_grade_if_promoted,
      });
    } else if (action === "repeat") {
      updateDecision(studentId, { action, target_stage: preview.source_stage, target_grade: preview.source_grade });
    } else {
      updateDecision(studentId, { action, target_stage: null, target_grade: null });
    }
  };

  const counts = preview ? Object.values(decisions).reduce((acc, d) => {
    acc[d.action] = (acc[d.action] || 0) + 1;
    return acc;
  }, {}) : {};

  const execute = async () => {
    if (!preview) return;
    const ok = window.confirm(
      `سيتم إنشاء سجلات جديدة في العام ${targetYear} لـ ${(counts.promote || 0) + (counts.repeat || 0)} طالب. ` +
      `هذا الإجراء لا يغيّر أي شيء في العام الحالي (${ctx.year}). متابعة؟`
    );
    if (!ok) return;
    setExecuting(true);
    try {
      const payload = {
        stage: ctx.stage, year: ctx.year, grade: sourceGrade, target_year: targetYear,
        decisions: Object.entries(decisions).map(([student_id, d]) => ({ student_id, ...d })),
      };
      const { data } = await api.post("/promotion/execute", payload);
      setResult(data);
      toast.success("تم تنفيذ الترحيل بنجاح");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setExecuting(false); }
  };

  const openOps = () => {
    setOpsOpen(true);
    setOpsLoading(true);
    api.get("/promotion/operations").then(({ data }) => setOps(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setOpsLoading(false));
  };

  const undoOp = async (op) => {
    if (!window.confirm("هل أنت متأكد من التراجع عن هذه العملية؟ سيتم حذف كل الطلاب الذين أُنشِئوا بها في العام المستهدَف.")) return;
    try {
      await api.post(`/promotion/operations/${op.operation_id}/undo`);
      toast.success("تم التراجع عن العملية");
      openOps();
    } catch (e) {
      if (e?.response?.status === 409) {
        const force = window.confirm(
          (e.response.data?.detail || "تم إدخال درجات بالفعل لبعض الطلاب.") + "\n\nهل تريد المتابعة والتراجع رغم ذلك؟ ستُفقَد هذه الدرجات."
        );
        if (force) {
          try {
            await api.post(`/promotion/operations/${op.operation_id}/undo`, null, { params: { force: true } });
            toast.success("تم التراجع عن العملية");
            openOps();
          } catch (e2) { toast.error(formatApiError(e2)); }
        }
      } else {
        toast.error(formatApiError(e));
      }
    }
  };

  return (
    <div className="space-y-6" data-testid="promotion-page">
      <PageHeader
        icon={ArrowUpRight}
        overline="بداية العام الدراسي"
        title="ترحيل الطلاب لعام جديد"
        subtitle="مراجعة نتيجة كل طالب وترحيله للصف التالي، أو إعادته، أو استبعاده — قبل أي تنفيذ فعلي."
        actions={
          <Button variant="outline" onClick={openOps} data-testid="promotion-history-btn">
            <History className="h-4 w-4 me-1.5" /> سجل عمليات الترحيل
          </Button>
        }
      />

      <div className="rounded-xl border border-border bg-white p-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
        <div className="space-y-1.5">
          <label className="text-[12px] font-medium text-muted-foreground">الصف المصدر (من {ctx.year})</label>
          <Select value={sourceGrade} onValueChange={setSourceGrade}>
            <SelectTrigger className="h-10" data-testid="promotion-source-grade"><SelectValue placeholder="اختر الصف" /></SelectTrigger>
            <SelectContent>
              {grades.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <label className="text-[12px] font-medium text-muted-foreground">العام المستهدَف</label>
          <Select value={targetYear} onValueChange={setTargetYear}>
            <SelectTrigger className="h-10" data-testid="promotion-target-year"><SelectValue /></SelectTrigger>
            <SelectContent>
              {years.filter((y) => y !== ctx.year).map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={runPreview} disabled={!sourceGrade || loading} className="h-10">
          {loading ? "جارٍ التحميل…" : "معاينة الترحيل"}
        </Button>
      </div>

      {preview && !result && (
        <>
          <div className="rounded-xl border border-border bg-white p-4">
            <div className="text-sm text-muted-foreground mb-1">
              الصف التالي عند النجاح: <span className="font-semibold text-primary">
                {preview.target_grade_if_promoted
                  ? `${preview.target_grade_if_promoted} (${preview.target_stage_if_promoted})`
                  : "لا يوجد — نهاية المسار الدراسي (تخرّج)"}
              </span>
            </div>
            <div className="flex flex-wrap gap-3 mt-3">
              <CountChip color="emerald" icon={ArrowUpRight} label="ترحيل" value={counts.promote || 0} />
              <CountChip color="amber" icon={RotateCcw} label="إعادة" value={counts.repeat || 0} />
              <CountChip color="indigo" icon={GraduationCap} label="تخرّج" value={counts.graduate || 0} />
              <CountChip color="slate" icon={Ban} label="مستبعَد" value={counts.exclude || 0} />
            </div>
          </div>

          <div className="rounded-xl border border-border bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-slate-50">
                    <th className="px-3 py-2.5 text-start font-semibold text-muted-foreground">الطالب</th>
                    <th className="px-3 py-2.5 text-center font-semibold text-muted-foreground">رقم الجلوس</th>
                    <th className="px-3 py-2.5 text-center font-semibold text-muted-foreground">النتيجة</th>
                    <th className="px-3 py-2.5 text-center font-semibold text-muted-foreground">الإجراء</th>
                    <th className="px-3 py-2.5 text-center font-semibold text-muted-foreground">الوجهة</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.students.map((s) => {
                    const d = decisions[s.student_id] || {};
                    return (
                      <tr key={s.student_id} className="border-b border-slate-100 last:border-0">
                        <td className="px-3 py-2">{s.name}</td>
                        <td className="px-3 py-2 text-center tabular-nums">{s.seat_number || "—"}</td>
                        <td className="px-3 py-2 text-center">
                          {s.passed ? (
                            <span className="inline-flex items-center gap-1 text-emerald-700 text-[12.5px] font-medium">
                              <CheckCircle2 className="h-3.5 w-3.5" /> ناجح
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-red-700 text-[12.5px] font-medium">
                              <XCircle className="h-3.5 w-3.5" /> غير ناجح
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <Select value={d.action} onValueChange={(v) => setActionFor(s.student_id, v)}>
                            <SelectTrigger className="h-8 w-[170px] mx-auto text-[12.5px]" data-testid={`promotion-action-${s.student_id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="promote" disabled={!preview.target_grade_if_promoted}>ترحيل للصف التالي</SelectItem>
                              <SelectItem value="repeat">إعادة نفس الصف</SelectItem>
                              <SelectItem value="graduate">تخرّج</SelectItem>
                              <SelectItem value="exclude">استبعاد (بلا نقل)</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-3 py-2 text-center text-[12.5px] text-muted-foreground">
                          {d.action === "promote" || d.action === "repeat" ? `${d.target_grade} (${d.target_stage})` : "—"}
                        </td>
                      </tr>
                    );
                  })}
                  {preview.students.length === 0 && (
                    <tr><td colSpan={5} className="px-3 py-10 text-center text-muted-foreground">لا يوجد طلاب في هذا الصف للعام الحالي</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={execute} disabled={executing || preview.students.length === 0} className="h-11 px-6" data-testid="promotion-execute-btn">
              {executing ? "جارٍ التنفيذ…" : `تنفيذ الترحيل إلى ${targetYear}`}
            </Button>
          </div>
        </>
      )}

      {result && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 flex items-start gap-3">
          <ShieldCheck className="h-8 w-8 text-emerald-600 shrink-0" />
          <div>
            <div className="font-semibold text-emerald-800 mb-1">تم تنفيذ الترحيل بنجاح</div>
            <div className="text-[13px] text-emerald-700">
              {result.promoted_count} طالب تم ترحيله، {result.repeated_count} إعادة، {result.graduated_count} تخرّج، {result.excluded_count} مستبعَد.
              <br />العام السابق ({ctx.year}) لم يتأثر إطلاقًا — كل هذا سجلات جديدة في {targetYear}.
            </div>
            <Button size="sm" variant="outline" className="mt-3" onClick={() => { setPreview(null); setResult(null); setSourceGrade(""); }}>
              ترحيل صف آخر
            </Button>
          </div>
        </div>
      )}

      {/* سجل عمليات الترحيل */}
      <Dialog open={opsOpen} onOpenChange={setOpsOpen}>
        <DialogContent className="max-w-2xl" dir="rtl">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              <History className="inline h-5 w-5 me-1.5" /> سجل عمليات الترحيل
            </DialogTitle>
            <DialogDescription>يمكن التراجع عن أي عملية — سيتم حذف الطلاب الذين أُنشِئوا بها فقط، دون أي تأثير على العام المصدر.</DialogDescription>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto">
            {opsLoading ? (
              <div className="text-center py-10 text-muted-foreground text-sm">جارٍ التحميل…</div>
            ) : ops.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground text-sm">لا توجد عمليات سابقة</div>
            ) : (
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border">
                    <th className="px-3 py-2 text-start font-semibold text-muted-foreground">التاريخ</th>
                    <th className="px-3 py-2 text-center font-semibold text-muted-foreground">من</th>
                    <th className="px-3 py-2 text-center font-semibold text-muted-foreground">إلى</th>
                    <th className="px-3 py-2 text-center font-semibold text-muted-foreground">ترحيل / إعادة / تخرّج</th>
                    <th className="px-3 py-2 text-start font-semibold text-muted-foreground">الحالة</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {ops.map((op) => (
                    <tr key={op.id} className="border-b border-slate-100 last:border-0">
                      <td className="px-3 py-2">{new Date(op.created_at).toLocaleDateString("ar-EG")}</td>
                      <td className="px-3 py-2 text-center">{op.source_grade}<br /><span className="text-muted-foreground">{op.source_year}</span></td>
                      <td className="px-3 py-2 text-center">{op.target_year}</td>
                      <td className="px-3 py-2 text-center tabular-nums">{op.promoted_count} / {op.repeated_count} / {op.graduated_count}</td>
                      <td className="px-3 py-2">
                        {op.status === "success" && <span className="text-emerald-700">تم بنجاح</span>}
                        {op.status === "undone" && <span className="text-slate-500">تم التراجع عنها</span>}
                      </td>
                      <td className="px-3 py-2 text-end">
                        {op.status === "success" && (
                          <Button size="sm" variant="outline" onClick={() => undoOp(op)} data-testid={`promotion-undo-${op.id}`}>
                            <Undo2 className="h-3.5 w-3.5 me-1" /> تراجع
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setOpsOpen(false)}>إغلاق</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CountChip({ icon: Icon, color, label, value }) {
  const colors = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    indigo: "border-indigo-200 bg-indigo-50 text-indigo-700",
    slate: "border-slate-200 bg-slate-50 text-slate-600",
  }[color];
  return (
    <div className={`rounded-lg border px-3.5 py-2 flex items-center gap-2 ${colors}`}>
      <Icon className="h-4 w-4" />
      <span className="text-[12.5px] font-medium">{label}:</span>
      <span className="font-display font-bold tabular-nums">{value}</span>
    </div>
  );
}
